import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../queryKeys';
import { orgApi } from '../api/org';
import { READINESS_EVIDENCE_PAGE_SIZE } from '../api/readiness';
import type { ReadinessEvidenceStatus } from '../api/readiness';
import { reviewApi } from '../api/review';

export function useOrgOverview() {
  return useQuery({
    queryKey: queryKeys.org.overview(),
    queryFn: orgApi.getOverview,
    staleTime: 60_000,
  });
}

export function useOrgBatches() {
  return useQuery({
    queryKey: queryKeys.org.batches(),
    queryFn: orgApi.getBatches,
    staleTime: 60_000,
  });
}

export function useOrgBatchDetail(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.batchDetail(batchId ?? ''),
    queryFn: () => orgApi.getBatchDetail(batchId as string),
    enabled: !!batchId,
    staleTime: 60_000,
  });
}

export function useOrgStudentDetail(batchId: string | undefined, userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.studentDetail(batchId ?? '', userId ?? ''),
    queryFn: () => orgApi.getStudentDetail(batchId as string, userId as string),
    enabled: !!batchId && !!userId,
    staleTime: 60_000,
  });
}

/**
 * The evidence behind a student's readiness components.
 *
 * Deliberately lazy: it is several joins server-side and most visits to a
 * student never drill in, so nothing is fetched until `enabled` flips true
 * (i.e. a TPO expands a row). One request covers every component, and the
 * cache entry is shared, so expanding a second row costs nothing.
 */
export function useOrgReadinessEvidence(
  batchId: string | undefined,
  userId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.org.studentReadinessEvidence(batchId ?? '', userId ?? ''),
    queryFn: () => orgApi.getStudentReadinessEvidence(batchId as string, userId as string),
    enabled: !!batchId && !!userId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * One readiness component's full record list, filtered and paged.
 *
 * Infinite rather than offset-swapping because a TPO reading a 327-topic list
 * is scanning, not paginating: they want the next fifty appended, and they
 * want their place kept. Changing the status or the search term is a different
 * query key, so it starts a fresh list rather than appending to an unrelated
 * one.
 */
export function useOrgReadinessComponentEvidence(
  batchId: string | undefined,
  userId: string | undefined,
  component: string | undefined,
  filters: { status?: ReadinessEvidenceStatus; q?: string } = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.org.studentReadinessComponentEvidence(
      batchId ?? '', userId ?? '', component ?? '', filters.status ?? 'all', filters.q ?? '',
    ),
    queryFn: ({ pageParam }) =>
      orgApi.getStudentReadinessComponentEvidence(
        batchId as string, userId as string, component as string,
        {
          status: filters.status,
          q: filters.q,
          limit: READINESS_EVIDENCE_PAGE_SIZE,
          offset: pageParam,
        },
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.items.length;
      // A short page means the server ran out, whatever `total` claims.
      return last.items.length > 0 && next < last.total ? next : undefined;
    },
    enabled: !!batchId && !!userId && !!component && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function useOrgBatchAssignments(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.batchAssignments(batchId ?? ''),
    queryFn: () => orgApi.getBatchAssignments(batchId as string),
    enabled: !!batchId,
    staleTime: 60_000,
  });
}

export function useOrgAssignmentDetail(batchId: string | undefined, assignmentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.assignmentDetail(batchId ?? '', assignmentId ?? ''),
    queryFn: () => orgApi.getAssignmentDetail(batchId as string, assignmentId as string),
    enabled: !!batchId && !!assignmentId,
    staleTime: 60_000,
  });
}

export function useOrgBatchAssessments(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.batchAssessments(batchId ?? ''),
    queryFn: () => orgApi.getBatchAssessments(batchId as string),
    enabled: !!batchId,
    staleTime: 60_000,
  });
}

export function useOrgAssessmentDetail(batchId: string | undefined, assessmentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.assessmentDetail(batchId ?? '', assessmentId ?? ''),
    queryFn: () => orgApi.getAssessmentDetail(batchId as string, assessmentId as string),
    enabled: !!batchId && !!assessmentId,
    staleTime: 60_000,
  });
}

export function useOrgBatchSessions(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.batchSessions(batchId ?? ''),
    queryFn: () => orgApi.getBatchSessions(batchId as string),
    enabled: !!batchId,
    staleTime: 60_000,
  });
}

export function useOrgSessionDetail(batchId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.sessionDetail(batchId ?? '', sessionId ?? ''),
    queryFn: () => orgApi.getSessionDetail(batchId as string, sessionId as string),
    enabled: !!batchId && !!sessionId,
    staleTime: 60_000,
  });
}

export function useOrgStudents() {
  return useQuery({
    queryKey: queryKeys.org.students(),
    queryFn: orgApi.getStudents,
    staleTime: 60_000,
  });
}

export function useOrgCompanyReadinessCompanies() {
  return useQuery({
    queryKey: queryKeys.org.companyReadinessCompanies(),
    queryFn: orgApi.getCompanyReadinessCompanies,
    staleTime: 5 * 60_000,
  });
}

export function useOrgCompanyReadiness(company: string | undefined, batchId: string) {
  return useQuery({
    queryKey: queryKeys.org.companyReadiness(company ?? '', batchId),
    queryFn: () => orgApi.getCompanyReadiness(company as string, batchId),
    enabled: !!company,
    staleTime: 60_000,
  });
}

export function useOrgStudentCompanyFit(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.org.studentCompanyFit(userId ?? ''),
    queryFn: () => orgApi.getStudentCompanyFit(userId as string),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useOrgInsights() {
  return useQuery({
    queryKey: queryKeys.org.insights(),
    queryFn: orgApi.getInsights,
    staleTime: 60_000,
  });
}

export function useOrgTrends() {
  return useQuery({
    queryKey: queryKeys.org.trends(),
    queryFn: orgApi.getTrends,
    staleTime: 60_000,
  });
}

export function useOrgStudentMutations(batchId: string, userId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.org.studentDetail(batchId, userId) });
  return {
    createNote: useMutation({
      mutationFn: (content: string) => orgApi.createNote(batchId, userId, content),
      onSuccess: invalidate,
    }),
    setShortlist: useMutation({
      mutationFn: (on: boolean) => orgApi.setShortlist(batchId, userId, on),
      onSuccess: invalidate,
    }),
    setFlag: useMutation({
      mutationFn: (on: boolean) => orgApi.setFlag(batchId, userId, on),
      onSuccess: invalidate,
    }),
  };
}

// --- management (batch / enrollment / trainer) ---

export function useOrgBatchManagement(batchId?: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: queryKeys.org.batches() });
    qc.invalidateQueries({ queryKey: queryKeys.org.overview() });
    qc.invalidateQueries({ queryKey: queryKeys.org.students() });
    if (batchId) {
      qc.invalidateQueries({ queryKey: queryKeys.org.batchDetail(batchId) });
      qc.invalidateQueries({ queryKey: ['org', 'assignable', batchId] });
    }
  };
  return {
    createBatch: useMutation({ mutationFn: orgApi.createBatch, onSuccess: invalidate }),
    updateBatch: useMutation({
      mutationFn: (v: { batchId: string; body: import('../api/org').OrgBatchInput }) =>
        orgApi.updateBatch(v.batchId, v.body),
      onSuccess: invalidate,
    }),
    archiveBatch: useMutation({ mutationFn: (id: string) => orgApi.archiveBatch(id), onSuccess: invalidate }),
    enrollStudent: useMutation({
      mutationFn: (v: { batchId: string; userId: string }) => orgApi.enrollStudent(v.batchId, v.userId),
      onSuccess: invalidate,
    }),
    removeStudent: useMutation({
      mutationFn: (v: { batchId: string; userId: string }) => orgApi.removeStudent(v.batchId, v.userId),
      onSuccess: invalidate,
    }),
    assignTrainer: useMutation({
      mutationFn: (v: { batchId: string; userId: string }) => orgApi.assignTrainer(v.batchId, v.userId),
      onSuccess: invalidate,
    }),
    removeTrainer: useMutation({
      mutationFn: (v: { batchId: string; userId: string }) => orgApi.removeTrainer(v.batchId, v.userId),
      onSuccess: invalidate,
    }),
  };
}

export function useAssignableStudents(batchId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['org', 'assignable', batchId],
    queryFn: () => orgApi.getAssignableStudents(batchId),
    enabled,
    staleTime: 30_000,
  });
}

export function useOrgTrainerPool(enabled: boolean) {
  return useQuery({
    queryKey: ['org', 'trainer-pool'],
    queryFn: orgApi.getOrgTrainers,
    enabled,
    staleTime: 60_000,
  });
}

export function useOrgProfile() {
  return useQuery({ queryKey: ['org', 'profile'], queryFn: orgApi.getOrgProfile, staleTime: 60_000 });
}

export function useUpdateOrgName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orgApi.updateOrgName,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', 'profile'] });
      qc.invalidateQueries({ queryKey: queryKeys.org.overview() });
    },
  });
}

/**
 * A batch's current review period.
 *
 * Snapshots are written once a week by the nightly job, so this is stable for
 * long stretches — a five-minute staleTime keeps a TPO clicking between
 * batches from refetching a result that provably cannot have changed.
 */
export function useOrgBatchReview(batchId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.org.batchReview(batchId ?? ''),
    queryFn: () => reviewApi.getBatchReview(batchId as string),
    enabled: !!batchId && (options?.enabled ?? true),
    staleTime: 300_000,
  });
}

/** One student's readiness trend across review periods. */
export function useOrgStudentReadinessHistory(
  batchId: string | undefined,
  userId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.org.studentReadinessHistory(batchId ?? '', userId ?? ''),
    queryFn: () => reviewApi.getStudentReadinessHistory(batchId as string, userId as string),
    enabled: !!batchId && !!userId && (options?.enabled ?? true),
    staleTime: 300_000,
  });
}
